import { useEffect, useRef, useState } from "react";
import { api } from "@/App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Building2, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * SupplierAutocomplete
 * --------------------
 * Searchable supplier picker backed by /api/suppliers.
 * - Stores supplier_id internally for clean relational data.
 * - Falls back to display name (legacy `supplier` text field) when ID is empty.
 * - Lets the user create a new supplier inline if none matches.
 *
 * Props:
 *  - supplierId: string | null
 *  - supplierName: string | null  (for legacy/display)
 *  - onChange: ({ supplier_id, supplier }) => void
 */
export default function SupplierAutocomplete({
  supplierId = "",
  supplierName = "",
  onChange,
  placeholder = "Buscar proveedor…",
  disabled = false,
  "data-testid": testId = "supplier-autocomplete",
}) {
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);

  // Initial load
  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, []);

  // Reflect external supplierName/supplierId into the query text
  useEffect(() => {
    if (supplierId) {
      const found = suppliers.find((s) => s.supplier_id === supplierId);
      setQuery(found?.name || supplierName || "");
    } else {
      setQuery(supplierName || "");
    }
  }, [supplierId, supplierName, suppliers]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = query.trim()
    ? suppliers.filter((s) => s.name.toLowerCase().includes(query.trim().toLowerCase()))
    : suppliers;

  const exactMatch = suppliers.find((s) => s.name.toLowerCase() === query.trim().toLowerCase());

  const pick = (sup) => {
    setQuery(sup.name);
    setOpen(false);
    onChange?.({ supplier_id: sup.supplier_id, supplier: sup.name });
  };

  const clear = () => {
    setQuery("");
    onChange?.({ supplier_id: "", supplier: "" });
  };

  const createSupplier = async () => {
    const name = query.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await api.post("/suppliers", { name });
      const created = res.data;
      setSuppliers((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      pick(created);
      toast.success("Proveedor creado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear proveedor");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-9 pr-9"
          data-testid={testId}
          data-supplier-input
        />
        {query && !disabled && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Limpiar"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</div>
          ) : (
            filtered.map((s) => {
              const isSelected = s.supplier_id === supplierId;
              return (
                <button
                  key={s.supplier_id}
                  type="button"
                  onClick={() => pick(s)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50 ${isSelected ? "bg-primary/5 font-medium" : ""}`}
                  data-testid={`supplier-option-${s.supplier_id}`}
                >
                  {isSelected ? <Check className="h-3.5 w-3.5 text-primary" /> : <span className="w-3.5" />}
                  <span className="truncate">{s.name}</span>
                </button>
              );
            })
          )}
          {query.trim() && !exactMatch && (
            <div className="border-t p-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="w-full justify-start text-primary hover:text-primary"
                onClick={createSupplier}
                disabled={creating}
                data-testid="create-supplier-inline-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                {creating ? "Creando…" : `Crear "${query.trim()}"`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
